import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ArrowRight, Play } from 'lucide-react';
import { getGalleryItems } from '../services/api';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Gallery = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [allGalleryItems, setAllGalleryItems] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        
        // Fetch images
        const images = await getGalleryItems({ type: 'image' });
        
        // Fetch videos with type 'gallery'
        const videoResponse = await axios.get(`${API}/api/videos`);
        const videos = videoResponse.data.filter(v => v.type === 'gallery' && v.isActive);
        
        setGalleryImages(images);
        setGalleryVideos(videos);
        
        // Combine and sort by order
        const combined = [
          ...images.map(img => ({ ...img, mediaType: 'image' })),
          ...videos.map(vid => ({ ...vid, mediaType: 'video' }))
        ].sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setAllGalleryItems(combined);
      } catch (error) {
        console.error('Failed to fetch gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = (currentIndex - 1 + allGalleryItems.length) % allGalleryItems.length;
    setCurrentIndex(newIndex);
    const item = allGalleryItems[newIndex];
    if (item.mediaType === 'video') {
      setSelectedVideo(item);
      setSelectedImage(null);
    } else {
      setSelectedImage(item);
      setSelectedVideo(null);
    }
  };

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % allGalleryItems.length;
    setCurrentIndex(newIndex);
    const item = allGalleryItems[newIndex];
    if (item.mediaType === 'video') {
      setSelectedVideo(item);
      setSelectedImage(null);
    } else {
      setSelectedImage(item);
      setSelectedVideo(null);
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white text-xl">Loading gallery...</p>
        </div>
      </section>
    );
  }

  // Helper to get YouTube thumbnail
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    let videoId = null;
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  // Limit to 8 items on desktop for preview, show all on mobile or when showAll is true
  const displayItems = showAll ? allGalleryItems : allGalleryItems.slice(0, 8);

  return (
    <section className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-black text-white">
            Our Treasure
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-200 mx-auto"></div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {displayItems.map((item, index) => (
            <div
              key={item.id || item._id}
              className="group relative h-64 rounded-xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-500"
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.05}s both`
              }}
              onClick={() => {
                if (item.mediaType === 'video') {
                  setSelectedVideo(item);
                } else {
                  openLightbox(item, index);
                }
              }}
            >
              {/* Image or Video Thumbnail */}
              {item.mediaType === 'video' ? (
                <>
                  <img
                    src={getYouTubeThumbnail(item.url) || '/placeholder-video.jpg'}
                    alt={item.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/60 transition-colors">
                    <div className="w-16 h-16 rounded-full bg-yellow-400/90 flex items-center justify-center transform group-hover:scale-110 transition-transform">
                      <Play size={28} className="text-black ml-1" fill="currentColor" />
                    </div>
                  </div>
                </>
              ) : (
                <img
                  src={item.url}
                  alt={item.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
              )}

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>

              {/* Title */}
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-white font-black text-xl">{item.title}</h3>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Jump to Home Button */}
        {allGalleryItems.length > 8 && (
          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/')}
              className="inline-flex items-center space-x-2 bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-4 rounded-full font-bold transform hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-400/50"
            >
              <span>JUMP TO HOME</span>
              <ArrowRight size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4">
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-yellow-400 transition-colors z-10"
            aria-label="Close"
          >
            <X size={32} />
          </button>

          {/* Previous Button */}
          <button
            onClick={goToPrevious}
            className="absolute left-6 top-1/2 -translate-y-1/2 bg-yellow-400 hover:bg-yellow-300 text-black p-3 rounded-full shadow-lg transform hover:scale-110 transition-all duration-300"
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Next Button */}
          <button
            onClick={goToNext}
            className="absolute right-6 top-1/2 -translate-y-1/2 bg-yellow-400 hover:bg-yellow-300 text-black p-3 rounded-full shadow-lg transform hover:scale-110 transition-all duration-300"
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>

          {/* Image */}
          <div className="max-w-5xl w-full">
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
            <p className="text-white text-center mt-4 text-xl font-bold">{selectedImage.title}</p>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4">
          <button
            onClick={() => setSelectedVideo(null)}
            className="absolute top-6 right-6 text-white hover:text-yellow-400 transition-colors z-10"
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <div className="max-w-5xl w-full aspect-video">
            <iframe
              src={selectedVideo.url.replace('watch?v=', 'embed/')}
              title={selectedVideo.title}
              className="w-full h-full rounded-2xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            <p className="text-white text-center mt-4 text-xl font-bold">{selectedVideo.title}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;